// Layer 4 (UI) — the PM Schedule tab (a20-maintenance S5a; polish.P21 comp A
// with variant B's work lanes as its first region). The lanes draw the open
// plan on one day axis; the schedule card below keeps the rows in due order
// behind a ghost control that reveals the last 30 days' completed instances.
// Every figure on the glass comes from PmSchedule.transform.ts and
// PmLanes.transform.ts over the seed and session cursor.
//
// The four KPI cards moved into the route's one status band (adjudication 4);
// Pending from Advisory moved up to the panel row (adjudication 2); the
// Production window panel became the header's constraint fact (adjudication 3).
// Planned and unplanned event ratios live on Downtime.

import { useState } from 'react';
import {
  Button,
  CitationChip,
  Pill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { DowntimeCard } from '../downtime/DowntimeCard';
import { EDGE_TABLE } from './MaintenanceTable';
import { formatHours } from './Downtime.transform';
import { PM_SCHEDULE_SYN_TOOLTIP } from './Scene.transform';
import {
  completedPmRows,
  pmTaskRows,
  pmTasks,
  type CompletedPmRow,
  type PmStatus,
} from './PmSchedule.transform';
import { PmLanes } from './PmLanes';
import { pmLanes } from './PmLanes.transform';
import { shutdownWindows } from './Strategy.transform';

const STATUS_TONE: Record<PmStatus, 'default' | 'danger' | 'success' | 'warning'> = {
  complete: 'success',
  overdue: 'danger',
  'due-today': 'warning',
  scheduled: 'default',
};

/** Completed rows append below a hairline so the two populations stay legible
 *  as two populations — the open plan above, the record below. */
const RECORD_DIVIDER = 'border-t-2 border-[var(--hairline)]';


export function PmScheduleTab() {
  const { state } = useScenario();
  const cursor = state.cursor;
  const tasks = pmTasks();
  const open = pmTaskRows(tasks, cursor);
  const completed = completedPmRows(tasks);
  const lanes = pmLanes({ tasks, windows: shutdownWindows(), state });
  const [expanded, setExpanded] = useState(false);

  return (
    <Commentable name="PmSchedule">
      <div className="space-y-3">
        <DowntimeCard title="The next 14 days" meta="tasks at the day shift of their due date · width = hours">
          <PmLanes read={lanes} />
        </DowntimeCard>

        <DowntimeCard
          title="Schedule"
          meta="next 14 days and open items"
          actions={
            <>
              <span title={PM_SCHEDULE_SYN_TOOLTIP}>
                <CitationChip label="inferred" confidence="inferred" />
              </span>
              <Button variant="ghost" onClick={() => setExpanded((show) => !show)}>
                {expanded ? 'Show fewer' : `Show all ${completed.length} completed`}
              </Button>
            </>
          }
          className="px-0 pb-1 [&>header]:px-4"
        >
          <div className={EDGE_TABLE}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH className="whitespace-nowrap">Due</TH>
                  <TH>Asset</TH>
                  <TH>Task</TH>
                  <TH className="whitespace-nowrap">Crew</TH>
                  <TH className="whitespace-nowrap text-right">Hrs</TH>
                  <TH>Status</TH>
                  {expanded ? <TH className="whitespace-nowrap">Done</TH> : null}
                </TR>
              </THead>
              <TBody>
                {open.map((row) => (
                  <TR key={row.id} title={row.id}>
                    <TD
                      className={`tabular-nums whitespace-nowrap ${
                        row.overdue ? 'font-semibold !text-[var(--state-blocked)]' : ''
                      }`}
                    >
                      {row.due}
                    </TD>
                    <TD className="whitespace-nowrap !text-ink-primary">{row.asset}</TD>
                    <TD>{row.task}</TD>
                    <TD className="whitespace-nowrap">{row.crew}</TD>
                    <TD className="tabular-nums whitespace-nowrap text-right">
                      {formatHours(row.hours)}
                    </TD>
                    <TD>
                      <Pill tone={STATUS_TONE[row.status]}>{row.statusLabel}</Pill>
                    </TD>
                    {expanded ? <TD /> : null}
                  </TR>
                ))}
                {expanded
                  ? completed.map((row, index) => (
                      <CompletedRow key={row.id} row={row} first={index === 0} />
                    ))
                  : null}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}

function CompletedRow({ row, first }: { row: CompletedPmRow; first: boolean }) {
  return (
    <TR className={first ? RECORD_DIVIDER : undefined} title={row.id}>
      <TD className="tabular-nums whitespace-nowrap">{row.due}</TD>
      <TD className="whitespace-nowrap">{row.asset}</TD>
      <TD>{row.task}</TD>
      <TD className="whitespace-nowrap">{row.crew}</TD>
      <TD className="tabular-nums whitespace-nowrap text-right">{formatHours(row.hours)}</TD>
      <TD>
        <Pill tone={STATUS_TONE[row.status]}>{row.statusLabel}</Pill>
      </TD>
      <TD
        className={`tabular-nums whitespace-nowrap ${
          row.late ? 'font-semibold !text-[var(--state-blocked)]' : ''
        }`}
      >
        {row.done}
      </TD>
    </TR>
  );
}
